/**
 * @file 文档页页左侧菜单
 * @author Wanzhongyun
 */

import React, { useCallback } from 'react';
import { Empty, Input, Menu, Tooltip } from 'antd';
import { MenuProps } from 'antd/es/menu';
import { ExclamationCircleFilled, SearchOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import classNames from 'classnames';

import LinkA from '../../../../components/a/LinkA';
import { IDocumentMenuItem } from '../../type';

// 高光匹配搜索字词
const getHighlightedText = (text: string, highlight: string) => {
    if (highlight === '') {
        return <span>{text}</span>;
    }
    const parts = text.split(new RegExp(`(${highlight})`, 'gi')).filter(e => e !== '');
    return (
        <>
            {parts.map((part, i) => (
                <span
                    key={i}
                    className={classNames({
                        'paddle-doc-page-left-menu-search-result-highlight':
                            part.toLowerCase() === highlight.toLowerCase()
                    })}
                >
                    {part}
                </span>
            ))}
        </>
    );
};

// 递归查找符合query的menu的数目
const sumDirectoryCount = (menuItem: IDocumentMenuItem, searchQuery: string): number => {
    const titleCount = +menuItem.title.toLowerCase().includes(searchQuery.toLowerCase());
    if (menuItem.children.length === 0) {
        return titleCount;
    }
    return menuItem.children.reduce((prev, curr) => {
        return prev + sumDirectoryCount(curr, searchQuery);
    }, titleCount);
};

const renderSubMenu = (menuList: IDocumentMenuItem[], shouldShowSearchResult: boolean, searchQuery: string) =>
    menuList.map(menuItem => {
        const isDataset = menuItem.title === 'paddle.dataset';
        const isFluid = menuItem.title === 'paddle.fluid';
        if (menuItem.children.length > 0) {
            return (
                <Menu.SubMenu
                    key={menuItem.key}
                    icon={<i className="paddle-doc-page-left-menu-arrow" />}
                    title={
                        <LinkA to={menuItem.link || ''}>
                            {getHighlightedText(menuItem.title, searchQuery)}
                            {isDataset && (
                                <Tooltip
                                    placement="bottom"
                                    title="paddle.dataset 将在未来废弃，请使用paddle.version.dataset与paddle.text.datasets。"
                                >
                                    <ExclamationCircleFilled style={{ marginLeft: 5 }} />
                                </Tooltip>
                            )}
                            {isFluid && (
                                <Tooltip
                                    placement="bottom"
                                    title={
                                        window.location.pathname.includes('/2.5') ||
                                        window.location.pathname.includes('/develop')
                                            ? 'paddle.fluid 目前已废弃，请使用其他可替代的API。'
                                            : 'paddle.fluid 将在未来废弃，请使用其他可替代的API。'
                                    }
                                >
                                    <ExclamationCircleFilled style={{ marginLeft: 5 }} />
                                </Tooltip>
                            )}
                            {/* 只有在paddlepaddl的api文档页面且搜索词条不为空才显示匹配项 */}
                            {shouldShowSearchResult && (
                                <div className="paddle-doc-page-left-menu-search-result">{`(${sumDirectoryCount(
                                    menuItem,
                                    searchQuery
                                )}个)`}</div>
                            )}
                        </LinkA>
                    }
                >
                    {renderSubMenu(menuItem.children, shouldShowSearchResult, searchQuery)}
                </Menu.SubMenu>
            );
        }
        return (
            <Menu.Item key={menuItem.key}>
                <LinkA to={menuItem.link || ''}>{getHighlightedText(menuItem.title, searchQuery)}</LinkA>
            </Menu.Item>
        );
    });

export interface IDocLeftMenuProps {
    subMenuOpenKeys: string[];
    subMenuSelectedKeys: string[];
    filteredSubMenuList: IDocumentMenuItem[];
    isApiDocumentation: boolean;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    setSubMenuOpenKeys: (openKeys: string[]) => void;
}

const DocLeftMenu: React.FC<IDocLeftMenuProps> = props => {
    const {
        filteredSubMenuList,
        subMenuOpenKeys,
        subMenuSelectedKeys,
        isApiDocumentation,
        searchQuery,
        setSearchQuery,
        setSubMenuOpenKeys
    } = props;

    const debouncedSearch = debounce((input: string) => setSearchQuery(input), 320);

    const onOpenChange = useCallback<Exclude<MenuProps['onOpenChange'], undefined>>(
        keys => {
            setSubMenuOpenKeys(keys.map(k => '' + k));
        },
        [setSubMenuOpenKeys]
    );

    return (
        <>
            {/* // 只有在paddlepaddl的api文档页面才显示搜索栏 */}
            {isApiDocumentation && (
                <Input
                    placeholder="输入API名称"
                    onChange={e => debouncedSearch(e.target.value)}
                    className="paddle-doc-page-search-bar"
                    prefix={<SearchOutlined />}
                />
            )}
            {filteredSubMenuList.length > 0 ? (
                <Menu
                    className="paddle-doc-page-left-menu"
                    mode="inline"
                    selectedKeys={subMenuSelectedKeys}
                    inlineIndent={16}
                    openKeys={subMenuOpenKeys}
                    onOpenChange={onOpenChange}
                    expandIcon={<React.Fragment />}
                >
                    {renderSubMenu(filteredSubMenuList, isApiDocumentation && searchQuery !== '', searchQuery)}
                </Menu>
            ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'无搜索结果'} />
            )}
        </>
    );
};

export default DocLeftMenu;
